import React, { MutableRefObject } from 'react';
import * as ToggleGrp from '@radix-ui/react-toggle-group';
import { Typography } from '../../index';

export type AdvancedOptionType = {
	id: string;
	label: React.ReactNode;
	disabled?: boolean;
};

type OptionType = string | AdvancedOptionType;

interface IToggleGroupProps {
	tabs?: boolean;
	noUnselect?: boolean;
	value: string;
	defaultValue?: string;
	options: OptionType[];
	className?: string;
	groupRef?: MutableRefObject<HTMLDivElement | null>;
	wrapperClassName?: string;
	setValue: (value: string) => void;
}

const formatOptions = (options: OptionType[]): AdvancedOptionType[] =>
	options.map((option) => (typeof option === 'string' ? { label: option, id: option, disabled: false } : option));

export const ToggleGroup = ({
	groupRef,
	tabs = false,
	noUnselect = false,
	value,
	defaultValue,
	options,
	className,
	wrapperClassName,
	setValue,
}: IToggleGroupProps) => {
	const renderClasses = (isActive: boolean, tabsVariant: boolean, tabsCount: number) => {
		const borderIndicator = tabsCount > 1;
		return tabsVariant
			? `border-t-0 border-l-0 border-r-0 border-solid bg-transparent border-b-2 ${
					isActive && borderIndicator ? 'border-b-primary shadow-InnerWarning' : 'border-transparent'
			  }`
			: `border rounded ${
					isActive ? 'border-primary shadow-innerWarning' : 'border-gray-500 hover:border-gray-200'
			  }`;
	};
	const renderTextColor = (id: string, val: string, disabled?: boolean) => {
		if (disabled) return 'inactiveText';
		return id === val ? 'title' : 'lightText';
	};
	const formattedTabs = formatOptions(options);
	return (
		<ToggleGrp.Root
			ref={groupRef}
			type="single"
			className={`${wrapperClassName}`}
			defaultValue={defaultValue}
			value={value}
			onValueChange={(currValue: string) => {
				if (noUnselect && (!currValue || !value)) return;
				setValue(currValue);
			}}>
			{formattedTabs.map(({ label, id, disabled }) => {
				const isTabSelected = value === id;
				const tabsCount = formattedTabs.length;
				return (
					<ToggleGrp.Item
						key={id}
						data-cy={`toggle ${id}`}
						className={`${
							disabled ? 'pointer-events-none border-gray-400 shadow-none' : 'cursor-pointer'
						} bg-white border-solid group ${renderClasses(isTabSelected, tabs, tabsCount)} ${className}`}
						disabled={disabled}
						value={id}>
						<Typography
							size="baseLg"
							variant="title"
							color={renderTextColor(id, value, disabled)}
							className={`${id !== value && 'group-hover:text-gray-200'}`}>
							{label}
						</Typography>
					</ToggleGrp.Item>
				);
			})}
		</ToggleGrp.Root>
	);
};
